import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  IdentificationIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { AccordionSection } from '../../components/UI/AccordionSection';
import { Button } from '../../components/UI/Button';
import { ColorPickerField } from '../../components/UI/ColorPickerField';
import { Modal } from '../../components/UI/Modal';
import { ToggleField } from '../../components/UI/ToggleField';
import { ChannelTypeSelect } from '../../components/Atendimento/ChannelTypeSelect';
import { InboxCreateSupportFormModal } from '../../components/Atendimento/InboxCreateSupportFormModal';
import { SupportExternalChannelsSection } from '../../components/Atendimento/SupportExternalChannelsSection';
import { WhatsAppInboxConfigSection } from '../../components/Atendimento/WhatsAppInboxConfigSection';
import { WidgetLivePreview } from '../../components/Atendimento/WidgetLivePreview';
import { WidgetTtsVoiceField } from '../../components/Atendimento/WidgetTtsVoiceField';
import MultiSelect from '../../components/UI/MultiSelect';
import { agentBotService } from '../../services/agentBotService';
import { formService } from '../../services/formService';
import { inboxService } from '../../services/inboxService';
import { userService } from '../../services/userService';
import { isValidWhatsAppPhone, normalizeWhatsAppPhoneDigits } from '../../services/validationService';
import { AgentBot } from '../../types/agentBot';
import { FormSummary } from '../../types/form';
import { User } from '../../types/users';
import { Inbox, InboxCsatSettings, InboxFormBinding } from '../../types/inbox';
import { ChannelType, defaultEnterSendsMessage } from '../../types/atendimentoEnums';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';
import {
  buildEmbedScript,
  buildKnowWidgetInitExample,
  buildWidgetAvatarUrl,
  buildWidgetTestPageUrl,
  BOT_REPLY_SPLIT_MAX_CHARACTERS_LIMIT,
  BOT_REPLY_SPLIT_MAX_DELAY_MS,
  BOT_REPLY_SPLIT_MIN_CHARACTERS,
  BOT_REPLY_SPLIT_MIN_DELAY_MS,
  DEFAULT_BOT_REPLY_SPLIT_DELAY_MS,
  DEFAULT_BOT_REPLY_SPLIT_MAX_CHARACTERS,
  DEFAULT_LAUNCHER_LABEL,
  DEFAULT_PRE_CHAT_MESSAGE,
  DEFAULT_INBOX_BUSINESS_HOURS,
  BUSINESS_HOUR_WEEKDAYS,
  EmailInboxConfiguration,
  formatInboxBusinessHoursSummary,
  formatInboxBusinessHoursTimeZoneLabel,
  getDefaultConfiguration,
  getDefaultPreChatFields,
  getInboxBusinessHoursWindow,
  InboxBusinessHours,
  InboxConfigurationBase,
  InstagramInboxConfiguration,
  MessengerInboxConfiguration,
  KEEP_EXISTING_SECRET_HINT,
  parseInboxConfiguration,
  resolveApiRootUrl,
  resolveBotReplySplitDelayMs,
  resolveBotReplySplitMaxCharacters,
  serializeInboxConfiguration,
  validateWidgetAvatarFile,
  WebchatInboxConfiguration,
  WhatsAppInboxConfiguration,
} from '../../types/inboxConfiguration';
import { useToast } from '../../components/UI/ToastProvider';
import { useMenuPermissions } from '../../hooks/useMenuPermissions';

const FORM_EDITOR_ROLES = ['admin', 'manager', 'editor'];

const apiRootUrl = resolveApiRootUrl();

type InboxEditTab = 'geral' | 'notificacoes';

type InboxFormState = {
  name: string;
  channelType: ChannelType;
  csatEnabled: boolean;
  csatSettings: InboxCsatSettings;
  sendClosingMessageOnAutoResolve: boolean;
  closingMessage: string;
  isActive: boolean;
  memberUserIds: string[];
};

type FormSnapshot = {
  form: InboxFormState;
  configurationJson: string;
  selectedBotId: string;
  linkedForms: InboxFormBinding[];
};

const DEFAULT_CSAT_SETTINGS: InboxCsatSettings = {
  message: '',
  askComment: true,
  offerOnAutoResolve: false,
  displayType: 'emoji',
  whatsAppTemplateName: '',
  whatsAppTemplateLanguage: 'pt_BR',
};

const DEFAULT_CLOSING_MESSAGE_PLACEHOLDER =
  'Olá! Como não tivemos retorno há algum tempo, estamos encerrando este atendimento por aqui para manter nossa fila organizada.\n\nSe precisar de algo ou quiser retomar a conversa, basta nos enviar uma nova mensagem por este mesmo canal a qualquer momento!\n\nPara outros assuntos, você também pode falar com a gente pelos e-mails:\n• Atendimento Comercial: comercial@baruk.ai\n• Suporte Técnico: suporte@baruk.ai\n\nTenha um excelente dia!';

const DEFAULT_FORM: InboxFormState = {
  name: '',
  channelType: ChannelType.WebWidget,
  csatEnabled: false,
  csatSettings: DEFAULT_CSAT_SETTINGS,
  sendClosingMessageOnAutoResolve: false,
  closingMessage: '',
  isActive: true,
  memberUserIds: [],
};

function normalizeCsatSettings(settings?: InboxCsatSettings | null): InboxCsatSettings {
  const displayType = settings?.displayType;
  return {
    message: settings?.message?.trim() ?? '',
    askComment: settings?.askComment ?? true,
    offerOnAutoResolve: settings?.offerOnAutoResolve ?? false,
    displayType: displayType === 'star' || displayType === 'number' || displayType === 'emoji'
      ? displayType
      : 'emoji',
    whatsAppTemplateName: settings?.whatsAppTemplateName?.trim() ?? '',
    whatsAppTemplateLanguage: settings?.whatsAppTemplateLanguage?.trim() || 'pt_BR',
  };
}

function parseAllowedDomainsText(value: string): string[] {
  return value
    .split(/[\n,;]+/)
    .map(item => item.trim())
    .filter(item => item.length > 0);
}

function withBusinessHoursWindow(
  hours: InboxBusinessHours | undefined,
  patch: { enabled?: boolean; days?: number[]; start?: string; end?: string }
): InboxBusinessHours {
  const window = getInboxBusinessHoursWindow(hours);
  return {
    enabled: patch.enabled ?? Boolean(hours?.enabled),
    timeZoneId: hours?.timeZoneId ?? null,
    windows: [
      {
        days: patch.days ?? window.days,
        start: patch.start ?? window.start,
        end: patch.end ?? window.end,
      },
    ],
  };
}

type WidgetFormPresentationRole = 'onDemand' | 'preChat' | 'onStart' | 'afterFirstMessage';

function getWidgetFormPresentationRole(item: InboxFormBinding): WidgetFormPresentationRole {
  if (item.isPreChat)
  {
    return 'preChat';
  }

  if (item.presentOnStart)
  {
    return 'onStart';
  }

  if (item.presentAfterFirstMessage)
  {
    return 'afterFirstMessage';
  }

  return 'onDemand';
}

function getWidgetFormPresentationRoleLabel(role: WidgetFormPresentationRole): string | null {
  switch (role)
  {
    case 'preChat':
      return 'Pré-chat';
    case 'onStart':
      return 'Ao iniciar';
    case 'afterFirstMessage':
      return 'Após 1ª mensagem';
    default:
      return null;
  }
}

function applyWidgetFormPresentationRole(
  forms: InboxFormBinding[],
  formId: string,
  role: WidgetFormPresentationRole
): InboxFormBinding[] {
  return forms.map((linked) => {
    const isSelected = linked.formId === formId;

    if (role === 'onDemand')
    {
      if (!isSelected)
      {
        return linked;
      }

      return {
        ...linked,
        isPreChat: false,
        presentOnStart: false,
        presentAfterFirstMessage: false,
        blockMessagesUntilAnswered: false,
      };
    }

    if (role === 'preChat')
    {
      const presentOnStart = isSelected ? false : linked.presentOnStart;
      const presentAfterFirstMessage = isSelected ? false : linked.presentAfterFirstMessage;
      return {
        ...linked,
        isPreChat: isSelected,
        presentOnStart,
        presentAfterFirstMessage,
        blockMessagesUntilAnswered:
          presentOnStart || presentAfterFirstMessage
            ? linked.blockMessagesUntilAnswered
            : false,
      };
    }

    if (role === 'onStart')
    {
      const presentOnStart = isSelected;
      const presentAfterFirstMessage = isSelected ? false : linked.presentAfterFirstMessage;
      return {
        ...linked,
        presentOnStart,
        isPreChat: isSelected ? false : linked.isPreChat,
        presentAfterFirstMessage,
        blockMessagesUntilAnswered: isSelected
          ? true
          : presentAfterFirstMessage
            ? linked.blockMessagesUntilAnswered
            : false,
      };
    }

    const presentAfterFirstMessage = isSelected;
    const presentOnStart = isSelected ? false : linked.presentOnStart;
    return {
      ...linked,
      presentAfterFirstMessage,
      presentOnStart,
      isPreChat: isSelected ? false : linked.isPreChat,
      blockMessagesUntilAnswered: isSelected
        ? true
        : presentOnStart
          ? linked.blockMessagesUntilAnswered
          : false,
    };
  });
}

function snapshotsEqual(a: FormSnapshot, b: FormSnapshot): boolean {
  if (a.configurationJson !== b.configurationJson || a.selectedBotId !== b.selectedBotId)
  {
    return false;
  }

  if (JSON.stringify(a.linkedForms) !== JSON.stringify(b.linkedForms))
  {
    return false;
  }

  if (
    a.form.name !== b.form.name
    || a.form.channelType !== b.form.channelType
    || a.form.csatEnabled !== b.form.csatEnabled
    || a.form.csatSettings.message !== b.form.csatSettings.message
    || a.form.csatSettings.askComment !== b.form.csatSettings.askComment
    || a.form.csatSettings.offerOnAutoResolve !== b.form.csatSettings.offerOnAutoResolve
    || a.form.csatSettings.displayType !== b.form.csatSettings.displayType
    || (a.form.csatSettings.whatsAppTemplateName ?? '') !== (b.form.csatSettings.whatsAppTemplateName ?? '')
    || (a.form.csatSettings.whatsAppTemplateLanguage ?? '') !== (b.form.csatSettings.whatsAppTemplateLanguage ?? '')
    || a.form.sendClosingMessageOnAutoResolve !== b.form.sendClosingMessageOnAutoResolve
    || a.form.closingMessage !== b.form.closingMessage
    || a.form.isActive !== b.form.isActive
  )
  {
    return false;
  }

  if (a.form.memberUserIds.length !== b.form.memberUserIds.length)
  {
    return false;
  }

  const left = [...a.form.memberUserIds].sort();
  const right = [...b.form.memberUserIds].sort();
  return left.every((value, index) => value === right[index]);
}

export const InboxWizardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { hasPermission } = useMenuPermissions();
  const canEditForms = hasPermission(FORM_EDITOR_ROLES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<InboxEditTab>('geral');
  const [users, setUsers] = useState<User[]>([]);
  const [bots, setBots] = useState<AgentBot[]>([]);
  const [selectedBotId, setSelectedBotId] = useState('');
  const originalBotId = useRef('');
  const [configurationJson, setConfigurationJson] = useState('{}');
  const [occupiedPhoneDigits, setOccupiedPhoneDigits] = useState<string[]>([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [baselineSnapshot, setBaselineSnapshot] = useState<FormSnapshot | null>(null);
  const [form, setForm] = useState<InboxFormState>(DEFAULT_FORM);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [persistedAvatarUrl, setPersistedAvatarUrl] = useState<string | undefined>();
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | undefined>();
  const [avatarError, setAvatarError] = useState<string | undefined>();
  const [allowedDomainsDraft, setAllowedDomainsDraft] = useState<string | null>(null);
  const [publishedForms, setPublishedForms] = useState<FormSummary[]>([]);
  const [linkedForms, setLinkedForms] = useState<InboxFormBinding[]>([]);
  const [formToLinkId, setFormToLinkId] = useState('');
  const [createFormModalOpen, setCreateFormModalOpen] = useState(false);

  const channelConfig = useMemo(() => {
    if (form.channelType === ChannelType.WebWidget)
    {
      return parseInboxConfiguration<WebchatInboxConfiguration>(configurationJson);
    }

    if (form.channelType === ChannelType.Whatsapp)
    {
      return parseInboxConfiguration<WhatsAppInboxConfiguration>(configurationJson);
    }

    if (form.channelType === ChannelType.Instagram)
    {
      return parseInboxConfiguration<InstagramInboxConfiguration>(configurationJson);
    }

    if (form.channelType === ChannelType.Messenger)
    {
      return parseInboxConfiguration<MessengerInboxConfiguration>(configurationJson);
    }

    if (form.channelType === ChannelType.Email)
    {
      return parseInboxConfiguration<EmailInboxConfiguration>(configurationJson);
    }

    return {};
  }, [configurationJson, form.channelType]);

  const webchatConfig = channelConfig as WebchatInboxConfiguration;
  const embedScript = webchatConfig.widgetToken
    ? buildEmbedScript(apiRootUrl, webchatConfig.widgetToken)
    : '';
  const knowWidgetInitExample = buildKnowWidgetInitExample();
  const widgetTestUrl = webchatConfig.widgetToken
    ? buildWidgetTestPageUrl(apiRootUrl, webchatConfig.widgetToken)
    : '';
  const inboxBaseConfig = parseInboxConfiguration<InboxConfigurationBase>(configurationJson);
  const enterSendsMessage =
    inboxBaseConfig.enterSendsMessage
    ?? defaultEnterSendsMessage(form.channelType);
  const botReplySplitEnabled = inboxBaseConfig.botReplySplit?.enabled === true;
  const botReplySplitMaxCharacters = resolveBotReplySplitMaxCharacters(inboxBaseConfig.botReplySplit);
  const botReplySplitDelayMs = resolveBotReplySplitDelayMs(inboxBaseConfig.botReplySplit);
  const isEmailChannel = form.channelType === ChannelType.Email;

  const isWidgetChannel = form.channelType === ChannelType.WebWidget;
  const toReplaceFormsPayload = () =>
    linkedForms.map((item, index) => ({
      formId: item.formId,
      sortOrder: index,
      presentOnStart: isWidgetChannel && item.presentOnStart,
      isPreChat: isWidgetChannel && item.isPreChat,
      presentAfterFirstMessage: isWidgetChannel && item.presentAfterFirstMessage,
      blockMessagesUntilAnswered:
        isWidgetChannel
        && item.blockMessagesUntilAnswered
        && (item.presentOnStart || item.presentAfterFirstMessage),
    }));
  const currentSnapshot: FormSnapshot = useMemo(
    () => ({
      form,
      configurationJson,
      selectedBotId,
      linkedForms,
    }),
    [form, configurationJson, selectedBotId, linkedForms]
  );
  const isDirty = (baselineSnapshot !== null && !snapshotsEqual(currentSnapshot, baselineSnapshot))
    || Boolean(avatarFile)
    || removeAvatar;

  const avatarUrl = removeAvatar ? undefined : (localAvatarUrl || persistedAvatarUrl);

  useEffect(() => {
    if (!avatarFile)
    {
      setLocalAvatarUrl(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(avatarFile);
    setLocalAvatarUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [avatarFile]);

  useEffect(() => {
    const load = async () => {
      try
      {
        const [usersResult, botsResult, inboxes, formsResult] = await Promise.all([
          userService.getUsers('', 1, 200),
          agentBotService.getAll(),
          inboxService.getAll(),
          formService.getAll(),
        ]);
        setUsers(usersResult.users);
        setBots(botsResult);
        setPublishedForms(formsResult.filter((item) =>
          item.status === 'Published'
          && item.accessType === 'Public'
          && item.acceptingResponses));
        setOccupiedPhoneDigits(collectOccupiedWhatsAppPhones(inboxes, id));

        if (isEdit && id)
        {
          const [inbox, inboxForms] = await Promise.all([
            inboxService.getById(id),
            inboxService.getForms(id),
          ]);
          const botId = inbox.agentBotId ?? '';
          const nextForm: InboxFormState = {
            name: inbox.name,
            channelType: inbox.channelType,
            csatEnabled: inbox.csatEnabled,
            csatSettings: normalizeCsatSettings(inbox.csatSettings),
            sendClosingMessageOnAutoResolve: inbox.sendClosingMessageOnAutoResolve ?? false,
            closingMessage: inbox.closingMessage ?? '',
            isActive: inbox.isActive,
            memberUserIds: inbox.memberUserIds,
          };
          const nextConfig = inbox.configuration || '{}';
          setForm(nextForm);
          setConfigurationJson(nextConfig);
          setSelectedBotId(botId);
          originalBotId.current = botId;
          const normalizedLinkedForms = inboxForms.map((item) => ({
            ...item,
            presentOnStart: Boolean(item.presentOnStart),
            isPreChat: Boolean(item.isPreChat),
            presentAfterFirstMessage: Boolean(item.presentAfterFirstMessage),
            blockMessagesUntilAnswered: Boolean(item.blockMessagesUntilAnswered),
          }));
          setLinkedForms(normalizedLinkedForms);
          setBaselineSnapshot({
            form: nextForm,
            configurationJson: nextConfig,
            selectedBotId: botId,
            linkedForms: normalizedLinkedForms,
          });
          const parsedWebchat = parseInboxConfiguration<WebchatInboxConfiguration>(nextConfig);
          if (parsedWebchat.hasAvatar && parsedWebchat.widgetToken)
          {
            setPersistedAvatarUrl(buildWidgetAvatarUrl(apiRootUrl, parsedWebchat.widgetToken, inbox.updatedAt));
          }
          else
          {
            setPersistedAvatarUrl(undefined);
          }
          setAvatarFile(null);
          setRemoveAvatar(false);
          setAvatarError(undefined);
          setAllowedDomainsDraft(null);
        }
        else
        {
          const nextConfig = serializeInboxConfiguration(getDefaultConfiguration(ChannelType.WebWidget));
          setConfigurationJson(nextConfig);
          setAllowedDomainsDraft(null);
          setLinkedForms([]);
          setBaselineSnapshot({
            form: DEFAULT_FORM,
            configurationJson: nextConfig,
            selectedBotId: '',
            linkedForms: [],
          });
        }
      }
      catch
      {
        addToast('Erro ao carregar dados', 'error');
      }
      finally
      {
        setLoading(false);
      }
    };

    void load();
  }, [id, isEdit, addToast]);

  const updateChannelConfig = <T extends object>(patch: Partial<T>) => {
    const current = parseInboxConfiguration<T>(configurationJson);
    setConfigurationJson(serializeInboxConfiguration({ ...current, ...patch }));
  };

  const updateBotReplySplit = (patch: { enabled?: boolean; maxCharacters?: number; delayMs?: number }) => {
    const current = parseInboxConfiguration<InboxConfigurationBase>(configurationJson);
    const nextEnabled = patch.enabled ?? current.botReplySplit?.enabled === true;
    const nextMax = patch.maxCharacters ?? resolveBotReplySplitMaxCharacters(current.botReplySplit);
    const nextDelay = patch.delayMs ?? resolveBotReplySplitDelayMs(current.botReplySplit);
    updateChannelConfig<InboxConfigurationBase>({
      botReplySplit: {
        enabled: nextEnabled,
        maxCharacters: Math.min(
          Math.max(nextMax, BOT_REPLY_SPLIT_MIN_CHARACTERS),
          BOT_REPLY_SPLIT_MAX_CHARACTERS_LIMIT
        ),
        delayMs: Math.min(
          Math.max(nextDelay, BOT_REPLY_SPLIT_MIN_DELAY_MS),
          BOT_REPLY_SPLIT_MAX_DELAY_MS
        ),
      },
    });
  };

  const handleChannelTypeChange = (channelType: ChannelType) => {
    setForm(f => ({ ...f, channelType }));
    setConfigurationJson(serializeInboxConfiguration(getDefaultConfiguration(channelType)));
    setAvatarFile(null);
    setRemoveAvatar(false);
    setPersistedAvatarUrl(undefined);
    setAvatarError(undefined);
    setAllowedDomainsDraft(null);
    if (channelType !== ChannelType.WebWidget)
    {
      setLinkedForms((prev) => prev.map((item) => ({
        ...item,
        presentOnStart: false,
        isPreChat: false,
        presentAfterFirstMessage: false,
        blockMessagesUntilAnswered: false,
      })));
    }
  };

  const memberOptions = useMemo(
    () => users.map(user => ({
      id: user.id,
      name: user.name,
      description: user.email,
    })),
    [users]
  );

  const syncBotAssignment = async (inboxId: string) => {
    if (selectedBotId === originalBotId.current)
    {
      return;
    }

    if (selectedBotId)
    {
      await agentBotService.assignToInbox(selectedBotId, { inboxId });
    }
    else if (originalBotId.current)
    {
      await inboxService.removeBotAssignment(inboxId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim())
    {
      addToast('Nome é obrigatório', 'error');
      return;
    }

    if (form.channelType === ChannelType.Whatsapp)
    {
      const whatsAppConfig = channelConfig as WhatsAppInboxConfiguration;
      if (!whatsAppConfig.businessAccountId?.trim())
      {
        addToast('Informe o ID da conta comercial (WABA)', 'error');
        return;
      }

      if (!isValidWhatsAppPhone(whatsAppConfig.phoneNumber))
      {
        addToast('Informe um número de WhatsApp válido com DDI (ex.: +5511999999999)', 'error');
        return;
      }

      const phoneDigits = normalizeWhatsAppPhoneDigits(whatsAppConfig.phoneNumber);
      if (occupiedPhoneDigits.includes(phoneDigits))
      {
        addToast('Já existe uma caixa de entrada WhatsApp com este número de telefone.', 'error');
        return;
      }
    }

    setSaving(true);
    try
    {
      let savedInbox: Inbox | undefined;
      if (isEdit && id)
      {
        savedInbox = await inboxService.update(id, {
          name: form.name.trim(),
          channelType: form.channelType,
          configuration: configurationJson,
          csatEnabled: form.csatEnabled,
          csatSettings: form.csatSettings,
          sendClosingMessageOnAutoResolve: form.sendClosingMessageOnAutoResolve,
          closingMessage: form.sendClosingMessageOnAutoResolve
            ? (form.closingMessage.trim() || null)
            : null,
          isActive: form.isActive,
        });
        await inboxService.updateMembers(id, { memberUserIds: form.memberUserIds });
        await syncBotAssignment(id);
        await inboxService.replaceForms(id, toReplaceFormsPayload());
        addToast('Caixa de entrada atualizada', 'success');
      }
      else
      {
        savedInbox = await inboxService.create({
          name: form.name.trim(),
          channelType: form.channelType,
          configuration: configurationJson,
          csatEnabled: form.csatEnabled,
          csatSettings: form.csatSettings,
          sendClosingMessageOnAutoResolve: form.sendClosingMessageOnAutoResolve,
          closingMessage: form.sendClosingMessageOnAutoResolve
            ? (form.closingMessage.trim() || null)
            : null,
          isActive: form.isActive,
          memberUserIds: form.memberUserIds,
        });
        if (savedInbox.configuration)
        {
          setConfigurationJson(savedInbox.configuration);
        }
        await syncBotAssignment(savedInbox.id);
        if (linkedForms.length > 0)
        {
          await inboxService.replaceForms(savedInbox.id, toReplaceFormsPayload());
        }
        addToast('Caixa de entrada criada', 'success');
      }

      if (isWidgetChannel && savedInbox)
      {
        try
        {
          if (avatarFile)
          {
            await inboxService.uploadWidgetAvatar(savedInbox.id, avatarFile);
          }
          else if (removeAvatar)
          {
            await inboxService.deleteWidgetAvatar(savedInbox.id);
          }
        }
        catch (avatarUploadError)
        {
          addToast(
            apiErrorMessage(avatarUploadError, 'Caixa salva, mas o avatar não pôde ser atualizado'),
            'error'
          );
        }
      }

      if (!isEdit && form.channelType === ChannelType.Whatsapp && savedInbox)
      {
        navigate(helpdeskRoutes.settingsInboxEdit(savedInbox.id));
        return;
      }

      navigate(helpdeskRoutes.settingsInboxes);
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao salvar caixa de entrada'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text: string, successMessage: string) => {
    if (!text)
    {
      return;
    }

    try
    {
      await navigator.clipboard.writeText(text);
      addToast(successMessage, 'success');
    }
    catch
    {
      addToast('Não foi possível copiar o trecho', 'error');
    }
  };

  const copyEmbedScript = () => {
    void copyToClipboard(embedScript, 'Script copiado');
  };

  const copyKnowWidgetInitExample = () => {
    void copyToClipboard(knowWidgetInitExample, 'Exemplo de init copiado');
  };

  if (loading)
  {
    return (
      <div className="flex justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  const showGeneralTab = activeTab === 'geral';
  const showNotificationsTab = isEdit && activeTab === 'notificacoes';
  const showChannelConfigSection =
    form.channelType === ChannelType.Whatsapp
    || form.channelType === ChannelType.Instagram
    || form.channelType === ChannelType.Messenger
    || form.channelType === ChannelType.Email;
  const submitDisabled = saving || (isEdit && !isDirty);
  const submitLabel = saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar caixa';

  const hoursEnabled = Boolean(webchatConfig.businessHours?.enabled);
  const hoursWindow = getInboxBusinessHoursWindow(webchatConfig.businessHours);
  const hoursCrossesMidnight = hoursWindow.start > hoursWindow.end;
  const hoursSameStartAndEnd = hoursWindow.start === hoursWindow.end;
  const hoursZoneLabel = formatInboxBusinessHoursTimeZoneLabel(webchatConfig.businessHours?.timeZoneId);
  const hoursNoticeDetail = `Nossa equipe responde ${formatInboxBusinessHoursSummary(webchatConfig.businessHours)} (${hoursZoneLabel}). Sua mensagem já foi registrada.`;
  const formsPreChat = linkedForms.find((item) => item.isPreChat);
  const previewProps = {
    headerTitle: webchatConfig.headerTitle,
    welcomeMessage: webchatConfig.welcomeMessage,
    launcherLabel: webchatConfig.launcherLabel ?? DEFAULT_LAUNCHER_LABEL,
    primaryColor: webchatConfig.primaryColor || '#2563eb',
    position: webchatConfig.position || 'right',
    voiceEnabled: webchatConfig.voiceEnabled !== false,
    alertsEnabled: webchatConfig.alertSoundEnabled === true || webchatConfig.alertNotificationEnabled === true,
    preChatFormEnabled: Boolean(formsPreChat) || Boolean(webchatConfig.preChatFormEnabled),
    preChatMessage: formsPreChat
      ? (formsPreChat.formTitle || 'Antes de começar, como podemos te chamar?')
      : (webchatConfig.preChatMessage || DEFAULT_PRE_CHAT_MESSAGE),
    preChatFields: formsPreChat
      ? [
          { name: 'fullName', label: 'Nome', type: 'text', placeholder: 'Seu nome', enabled: true, required: true },
          { name: 'email', label: 'E-mail', type: 'email', placeholder: 'Seu e-mail', enabled: true, required: true },
        ]
      : (webchatConfig.preChatFields?.length
        ? webchatConfig.preChatFields
        : getDefaultPreChatFields()),
    avatarUrl,
    presenceLabel: hoursEnabled ? 'Fora do expediente' : undefined,
    hoursNotice: hoursEnabled
      ? {
        headline: 'Fora do expediente',
        detail: hoursNoticeDetail,
      }
      : undefined,
  };

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col overflow-hidden ${
        isWidgetChannel && showGeneralTab ? 'max-w-6xl' : 'max-w-3xl'
      }`}
    >
      <div className="shrink-0">
        <Link to={helpdeskRoutes.settingsInboxes} className="text-sm text-blue-600 hover:underline mb-4 inline-block">
          ← Voltar para caixas
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mb-8">
          {isEdit ? 'Editar caixa de entrada' : 'Nova caixa de entrada'}
        </h1>

        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-1" aria-label="Abas da caixa de entrada">
            <button
              type="button"
              className={`px-6 py-3 font-medium ${
                activeTab === 'geral'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('geral')}
            >
              Geral
            </button>
            {isEdit && (
              <button
                type="button"
                className={`px-6 py-3 font-medium ${
                  activeTab === 'notificacoes'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('notificacoes')}
              >
                Notificações externas
              </button>
            )}
          </nav>
        </div>
      </div>

      {showNotificationsTab && id && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SupportExternalChannelsSection inboxId={id} agentBotId={selectedBotId || null} />
        </div>
      )}

      {showGeneralTab && (
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div
            className={`min-h-0 flex-1 overflow-y-auto ${
              isWidgetChannel ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-6 lg:items-start' : ''
            }`}
          >
            <div className="space-y-5 min-w-0 pb-4">
              <AccordionSection
                title="Dados da caixa"
                description="Nome, canal e status da caixa de entrada"
                icon={IdentificationIcon}
                defaultOpen
              >
                <ToggleField
                  label="Ativa"
                  description="Quando desativada, a caixa deixa de receber e exibir novas conversas na fila."
                  checked={form.isActive}
                  onChange={checked => setForm(f => ({ ...f, isActive: checked }))}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Ex.: WhatsApp SAC (11 9xxxx-xxxx)"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Use um nome que identifique o canal ou número para facilitar o filtro na fila de atendimento.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de canal</label>
                  <ChannelTypeSelect
                    value={form.channelType}
                    onChange={handleChannelTypeChange}
                  />
                </div>
              </AccordionSection>

              {isWidgetChannel && (
                <AccordionSection
                  title="Aparência"
                  description="Título, avatar, mensagem, cor e posição do widget"
                  icon={SwatchIcon}
                >
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Título do cabeçalho</label>
                    <input
                      type="text"
                      value={webchatConfig.headerTitle || ''}
                      onChange={e => updateChannelConfig<WebchatInboxConfiguration>({ headerTitle: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Avatar</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Imagem exibida no cabeçalho e no botão flutuante do widget. JPG, PNG, GIF ou WEBP, até 5 MB.
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50 text-2xl">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="Avatar do widget" className="h-full w-full object-cover" />
                        ) : (
                          <span aria-hidden>💬</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <input
                          id="widget-avatar-upload"
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="block w-full text-sm text-gray-600"
                          onChange={event => {
                            const selected = event.target.files?.[0] ?? null;
                            event.target.value = '';
                            if (!selected)
                            {
                              return;
                            }

                            const validationError = validateWidgetAvatarFile(selected);
                            if (validationError)
                            {
                              setAvatarError(validationError);
                              return;
                            }

                            setAvatarError(undefined);
                            setAvatarFile(selected);
                            setRemoveAvatar(false);
                          }}
                        />
                        {avatarUrl ? (
                          <Button
                            type="button"
                            variant="dangerOutline"
                            size="sm"
                            onClick={() => {
                              setAvatarFile(null);
                              setRemoveAvatar(true);
                              setAvatarError(undefined);
                            }}
                          >
                            Remover avatar
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    {avatarError ? (
                      <p className="mt-2 text-xs text-red-600">{avatarError}</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Mensagem de boas-vindas</label>
                    <textarea
                      value={webchatConfig.welcomeMessage || ''}
                      onChange={e => updateChannelConfig<WebchatInboxConfiguration>({ welcomeMessage: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows={2}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Use {'{{nome}}'} para o primeiro nome já conhecido no pré-chat ou enviado pelo site.
                      Exemplo: Olá, {'{{nome}}'}! Como podemos ajudar? Sem nome, esse trecho sai da frase.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Texto ao lado do botão</label>
                    <input
                      type="text"
                      value={webchatConfig.launcherLabel ?? DEFAULT_LAUNCHER_LABEL}
                      onChange={e => updateChannelConfig<WebchatInboxConfiguration>({ launcherLabel: e.target.value })}
                      placeholder={DEFAULT_LAUNCHER_LABEL}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Exibido ao lado do botão flutuante enquanto o chat está fechado. Deixe em branco para ocultar.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <ColorPickerField
                      label="Cor principal"
                      value={webchatConfig.primaryColor || '#2563eb'}
                      onChange={hex => updateChannelConfig<WebchatInboxConfiguration>({ primaryColor: hex })}
                    />
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Posição</label>
                      <select
                        value={webchatConfig.position || 'right'}
                        onChange={e => updateChannelConfig<WebchatInboxConfiguration>({ position: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="right">Direita</option>
                        <option value="left">Esquerda</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Botão flutuante (painel aberto)</label>
                      <select
                        value={webchatConfig.launcherCloseBehavior || 'minimize'}
                        onChange={e => updateChannelConfig<WebchatInboxConfiguration>({
                          launcherCloseBehavior: e.target.value as 'minimize' | 'resolve',
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="minimize">Minimizar</option>
                        <option value="resolve">Encerrar atendimento</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Em &quot;Encerrar&quot;, o visitante confirma antes de finalizar; o histórico desta conversa deixa de aparecer no widget.
                      </p>
                    </div>
                  </div>
                  <ToggleField
                    label="Permitir interação por voz"
                    description="Exibe microfone (transcrição) e alto-falante (leitura) no widget. O microfone pede permissão no site do cliente, não no Know."
                    checked={webchatConfig.voiceEnabled !== false}
                    onChange={checked => updateChannelConfig<WebchatInboxConfiguration>({
                      voiceEnabled: checked,
                    })}
                  />
                  {webchatConfig.voiceEnabled !== false ? (
                    <WidgetTtsVoiceField
                      ttsVoiceUri={webchatConfig.ttsVoiceUri}
                      ttsVoiceName={webchatConfig.ttsVoiceName}
                      onChange={next => updateChannelConfig<WebchatInboxConfiguration>({
                        ttsVoiceUri: next.ttsVoiceUri,
                        ttsVoiceName: next.ttsVoiceName,
                      })}
                    />
                  ) : null}
                  <ToggleField
                    label="Alerta sonoro"
                    description="Toca um som no site quando chega mensagem, formulário ou pesquisa e a página está visível. Com a aba em segundo plano, o som sai pela notificação do navegador."
                    checked={webchatConfig.alertSoundEnabled === true}
                    onChange={checked => updateChannelConfig<WebchatInboxConfiguration>({
                      alertSoundEnabled: checked,
                    })}
                  />
                  <ToggleField
                    label="Notificação do navegador"
                    description="Avisa o visitante com uma notificação do sistema. O navegador pede permissão na primeira vez que ele abre o chat."
                    checked={webchatConfig.alertNotificationEnabled === true}
                    onChange={checked => updateChannelConfig<WebchatInboxConfiguration>({
                      alertNotificationEnabled: checked,
                    })}
                  />
                  {webchatConfig.alertSoundEnabled === true || webchatConfig.alertNotificationEnabled === true ? (
                    <div>
                      <label className="block text-sm text-gray-700 mb-1" htmlFor="widget-alert-when">
                        Quando avisar
                      </label>
                      <select
                        id="widget-alert-when"
                        value={webchatConfig.alertWhen || 'hidden-tab'}
                        onChange={e => updateChannelConfig<WebchatInboxConfiguration>({
                          alertWhen: e.target.value as 'hidden-tab' | 'panel-closed' | 'split',
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="hidden-tab">Só com a aba em segundo plano</option>
                        <option value="panel-closed">Com o painel do chat fechado</option>
                        <option value="split">Som com o painel fechado; notificação só com a aba em segundo plano</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Vale para mensagem nova, formulário e pesquisa de satisfação. O visitante pode silenciar no próprio widget; essa escolha fica só no navegador dele.
                      </p>
                    </div>
                  ) : null}
                </AccordionSection>
              )}

              {isWidgetChannel && (
                <AccordionSection
                  title="Expediente da equipe"
                  description="Status e aviso no widget quando o visitante espera um humano"
                  icon={ClockIcon}
                >
                  <ToggleField
                    label="Definir horário de expediente"
                    description="O assistente (IA) continua respondendo fora do horário. O aviso aparece só quando a conversa estiver com a equipe."
                    checked={Boolean(webchatConfig.businessHours?.enabled)}
                    onChange={checked => updateChannelConfig<WebchatInboxConfiguration>({
                      businessHours: checked
                        ? withBusinessHoursWindow(
                          webchatConfig.businessHours?.windows?.length
                            ? webchatConfig.businessHours
                            : DEFAULT_INBOX_BUSINESS_HOURS,
                          { enabled: true }
                        )
                        : { ...webchatConfig.businessHours, enabled: false },
                    })}
                  />
                  {webchatConfig.businessHours?.enabled ? (
                    <div className="space-y-4 pt-1">
                      <div>
                        <p className="text-sm text-gray-700 mb-2">Dias</p>
                        <div className="flex flex-wrap gap-2">
                          {BUSINESS_HOUR_WEEKDAYS.map(day => {
                            const selected = getInboxBusinessHoursWindow(webchatConfig.businessHours).days.includes(day.id);
                            return (
                              <button
                                key={day.id}
                                type="button"
                                aria-pressed={selected}
                                className={`px-3 py-1.5 rounded-md text-sm border ${
                                  selected
                                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                                    : 'bg-white border-gray-200 text-gray-600'
                                }`}
                                onClick={() => {
                                  const current = getInboxBusinessHoursWindow(webchatConfig.businessHours);
                                  const days = selected
                                    ? current.days.filter(item => item !== day.id)
                                    : [...current.days, day.id];
                                  updateChannelConfig<WebchatInboxConfiguration>({
                                    businessHours: withBusinessHoursWindow(webchatConfig.businessHours, { days }),
                                  });
                                }}
                              >
                                {day.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Início</label>
                          <input
                            type="time"
                            value={getInboxBusinessHoursWindow(webchatConfig.businessHours).start}
                            onChange={e => updateChannelConfig<WebchatInboxConfiguration>({
                              businessHours: withBusinessHoursWindow(webchatConfig.businessHours, {
                                start: e.target.value || '08:00',
                              }),
                            })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Fim</label>
                          <input
                            type="time"
                            value={getInboxBusinessHoursWindow(webchatConfig.businessHours).end}
                            onChange={e => updateChannelConfig<WebchatInboxConfiguration>({
                              businessHours: withBusinessHoursWindow(webchatConfig.businessHours, {
                                end: e.target.value || '18:00',
                              }),
                            })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        <p className="font-medium">Fora do expediente</p>
                        <p className="mt-0.5">
                          {hoursNoticeDetail}
                        </p>
                        <p className="mt-1 text-xs text-amber-800/90">
                          O fuso padrão é o cadastrado no cliente. Se o término for menor que o início, o expediente atravessa a meia-noite (ex.: 22h às 6h).
                        </p>
                      </div>
                      {hoursSameStartAndEnd ? (
                        <p className="text-xs text-red-600">
                          Início e término iguais não formam um intervalo. Ajuste os horários.
                        </p>
                      ) : null}
                      {hoursCrossesMidnight ? (
                        <p className="text-xs text-slate-600">
                          Este expediente atravessa a meia-noite: a equipe fica disponível até o término no dia seguinte.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </AccordionSection>
              )}

              <AccordionSection
                title="Formulários no chat"
                description={
                  isWidgetChannel
                    ? 'Escolha quando cada formulário aparece no widget'
                    : 'O operador envia o link público do formulário'
                }
                icon={DocumentTextIcon}
              >
                {isWidgetChannel
                  && webchatConfig.preChatFormEnabled
                  && !linkedForms.some((item) => item.isPreChat) && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Esta caixa ainda usa o pré-chat legado na configuração do widget.
                    Vincule um formulário e marque-o como Pré-chat nesta seção para migrar;
                    o formulário embutido antigo deixa de ser a fonte da verdade.
                  </div>
                )}
                <p className="text-sm text-gray-600">
                  {isWidgetChannel ? (
                    <>
                      Vincule formulários <strong>públicos</strong>, publicados e aceitando respostas.
                      O visitante não tem login no Know — formulário privado não funciona aqui.
                      Escolha abaixo quando cada um aparece no chat.
                    </>
                  ) : (
                    <>
                      Só entram formulários públicos, publicados e aceitando respostas.
                      Neste canal o operador envia o link na conversa; disparos automáticos
                      (pré-chat, ao iniciar, após 1ª mensagem) ficam só no Widget.
                    </>
                  )}
                </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex-1">
                      <label className="mb-1 block text-sm text-gray-700">Adicionar formulário público</label>
                      <select
                        value={formToLinkId}
                        onChange={(e) => setFormToLinkId(e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="">Selecione...</option>
                        {publishedForms
                          .filter((item) => !linkedForms.some((linked) => linked.formId === item.id))
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.title}
                            </option>
                          ))}
                      </select>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        const selected = publishedForms.find((item) => item.id === formToLinkId);
                        if (!selected)
                        {
                          return;
                        }

                        setLinkedForms((prev) => [
                          ...prev,
                          {
                            formId: selected.id,
                            formTitle: selected.title,
                            formPublicId: selected.publicId,
                            formStatus: selected.status,
                            formAccessType: selected.accessType,
                            sortOrder: prev.length,
                            presentOnStart: false,
                            isPreChat: false,
                            presentAfterFirstMessage: false,
                            blockMessagesUntilAnswered: false,
                          },
                        ]);
                        setFormToLinkId('');
                      }}
                      disabled={!formToLinkId}
                    >
                      Vincular
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setCreateFormModalOpen(true)}
                    >
                      Criar formulário
                    </Button>
                  </div>

                  {linkedForms.length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum formulário vinculado.</p>
                  ) : (
                    <ul className="space-y-3">
                      {linkedForms.map((item, index) => {
                        const presentationRole = getWidgetFormPresentationRole(item);
                        const roleLabel = getWidgetFormPresentationRoleLabel(presentationRole);
                        const roleGroupName = `widget-form-role-${item.formId}`;
                        const showBlockOption =
                          presentationRole === 'onStart' || presentationRole === 'afterFirstMessage';

                        return (
                        <li
                          key={item.formId}
                          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-semibold text-gray-900">
                                  {item.formTitle || item.formId}
                                </p>
                                {isWidgetChannel && roleLabel ? (
                                  <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                                    {roleLabel}
                                  </span>
                                ) : null}
                              </div>
                              <p className="mt-0.5 text-xs text-gray-500">
                                {isWidgetChannel
                                  ? `Formulário vinculado · Ordem ${index + 1}`
                                  : 'Envio sob demanda, como link público.'}
                              </p>
                              {item.formAccessType === 'Private' && (
                                <p className="mt-1 text-xs text-amber-700">
                                  Privado: o visitante não consegue responder. Remova e vincule um formulário público.
                                </p>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              {canEditForms ? (
                                <Link
                                  to={`/forms/${item.formId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rounded-lg px-2 py-1 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50"
                                >
                                  Editar
                                </Link>
                              ) : null}
                              <button
                                type="button"
                                className="rounded-lg px-2 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                                onClick={() =>
                                  setLinkedForms((prev) =>
                                    prev
                                      .filter((linked) => linked.formId !== item.formId)
                                      .map((linked, sortOrder) => ({ ...linked, sortOrder }))
                                  )
                                }
                              >
                                Remover
                              </button>
                            </div>
                          </div>

                          {isWidgetChannel ? (
                            <fieldset className="mt-4 border-0 p-0">
                              <legend className="mb-2 text-sm font-medium text-gray-800">
                                Quando apresentar?
                              </legend>
                              <div
                                className="space-y-2"
                                role="radiogroup"
                                aria-label={`Quando apresentar ${item.formTitle || 'formulário'}`}
                              >
                                <label className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50">
                                  <input
                                    type="radio"
                                    name={roleGroupName}
                                    className="mt-0.5"
                                    checked={presentationRole === 'onDemand'}
                                    onChange={() =>
                                      setLinkedForms((prev) =>
                                        applyWidgetFormPresentationRole(prev, item.formId, 'onDemand')
                                      )
                                    }
                                  />
                                  <span>
                                    <span className="block text-sm text-gray-900">Só sob demanda</span>
                                    <span className="block text-xs text-gray-500">
                                      O operador envia o link quando quiser
                                    </span>
                                  </span>
                                </label>
                                <label className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50">
                                  <input
                                    type="radio"
                                    name={roleGroupName}
                                    className="mt-0.5"
                                    checked={presentationRole === 'preChat'}
                                    onChange={() =>
                                    {
                                      setLinkedForms((prev) =>
                                        applyWidgetFormPresentationRole(prev, item.formId, 'preChat')
                                      );
                                      updateChannelConfig<WebchatInboxConfiguration>({
                                        preChatFormEnabled: false,
                                      });
                                    }}
                                  />
                                  <span>
                                    <span className="block text-sm text-gray-900">Pré-chat</span>
                                    <span className="block text-xs text-gray-500">
                                      Exige o formulário antes de abrir o chat
                                    </span>
                                  </span>
                                </label>
                                <label className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50">
                                  <input
                                    type="radio"
                                    name={roleGroupName}
                                    className="mt-0.5"
                                    checked={presentationRole === 'onStart'}
                                    onChange={() =>
                                      setLinkedForms((prev) =>
                                        applyWidgetFormPresentationRole(prev, item.formId, 'onStart')
                                      )
                                    }
                                  />
                                  <span>
                                    <span className="block text-sm text-gray-900">Ao iniciar o atendimento</span>
                                    <span className="block text-xs text-gray-500">
                                      Mostra o formulário ao abrir a conversa
                                    </span>
                                  </span>
                                </label>
                                <label className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50">
                                  <input
                                    type="radio"
                                    name={roleGroupName}
                                    className="mt-0.5"
                                    checked={presentationRole === 'afterFirstMessage'}
                                    onChange={() =>
                                      setLinkedForms((prev) =>
                                        applyWidgetFormPresentationRole(
                                          prev,
                                          item.formId,
                                          'afterFirstMessage'
                                        )
                                      )
                                    }
                                  />
                                  <span>
                                    <span className="block text-sm text-gray-900">Após a 1ª mensagem</span>
                                    <span className="block text-xs text-gray-500">
                                      Aceita a mensagem do visitante, mostra o formulário e só então o robô responde
                                    </span>
                                  </span>
                                </label>
                              </div>

                              {showBlockOption ? (
                                <div className="ml-7 mt-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                                  <label className="flex cursor-pointer items-start gap-2.5">
                                    <input
                                      type="checkbox"
                                      className="mt-0.5"
                                      checked={item.blockMessagesUntilAnswered}
                                      onChange={(e) =>
                                      {
                                        const checked = e.target.checked;
                                        setLinkedForms((prev) =>
                                          prev.map((linked) => {
                                            if (linked.formId !== item.formId)
                                            {
                                              return linked;
                                            }

                                            return {
                                              ...linked,
                                              blockMessagesUntilAnswered: checked,
                                            };
                                          })
                                        );
                                      }}
                                    />
                                    <span>
                                      <span className="block text-sm text-gray-900">
                                        Bloquear mensagens até o visitante responder
                                      </span>
                                      <span className="block text-xs text-gray-500">
                                        O visitante só envia novas mensagens depois de concluir o formulário
                                      </span>
                                    </span>
                                  </label>
                                </div>
                              ) : null}
                            </fieldset>
                          ) : null}
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </AccordionSection>

              {isWidgetChannel && (
                <AccordionSection
                  title="Integração do widget"
                  description="Script de embed, identidade no site e assinatura HMAC"
                  icon={CodeBracketIcon}
                >
                  <ToggleField
                    label="Exigir assinatura do ID do usuário (HMAC)"
                    description="Recomendado em sites com login. Impede que alguém invente um ID de usuário no browser. O hash deve ser gerado no backend do cliente."
                    checked={Boolean(webchatConfig.hmacMandatory)}
                    onChange={checked => updateChannelConfig<WebchatInboxConfiguration>({
                      hmacMandatory: checked,
                    })}
                  />
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Domínios permitidos</label>
                    <textarea
                      value={allowedDomainsDraft ?? (webchatConfig.allowedDomains || []).join('\n')}
                      onChange={e => {
                        const text = e.target.value;
                        setAllowedDomainsDraft(text);
                        updateChannelConfig<WebchatInboxConfiguration>({
                          allowedDomains: parseAllowedDomainsText(text),
                        });
                      }}
                      onBlur={() => {
                        if (allowedDomainsDraft === null)
                        {
                          return;
                        }

                        const parsed = parseAllowedDomainsText(allowedDomainsDraft);
                        setAllowedDomainsDraft(null);
                        updateChannelConfig<WebchatInboxConfiguration>({
                          allowedDomains: parsed,
                        });
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                      rows={3}
                      placeholder={'loja.cliente.com\n*.cliente.com'}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Um host por linha (ex.: <code className="font-mono">app.cliente.com</code> ou <code className="font-mono">*.cliente.com</code>).
                      Lista vazia só libera localhost e o host da API (página de teste). Com a lista preenchida, só esses hosts entram — localhost deixa de ser exceção.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Segredo HMAC (para o backend do cliente)</label>
                    <input
                      type="text"
                      readOnly
                      value={webchatConfig.hmacToken || ''}
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50 font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Gerado ao salvar a caixa. O time de integração do cliente usa este segredo no servidor dele para calcular
                      a assinatura: <code className="font-mono">HMAC-SHA256(segredo, idDoUsuario)</code> em hex minúsculo,
                      e envia o resultado como <code className="font-mono">identifierHash</code> no <code className="font-mono">KnowWidget.init</code>.
                      Não fica exposto nas APIs públicas do widget. Na página de teste do Know, cole este valor só para o QA gerar o hash.
                    </p>
                  </div>
                  {webchatConfig.widgetToken && (
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Token do widget</label>
                      <input type="text" readOnly value={webchatConfig.widgetToken} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50" />
                    </div>
                  )}
                  {embedScript && (
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm text-gray-700">Script de embed</label>
                          <div className="flex gap-2">
                            {widgetTestUrl && (
                              <Button
                                variant="secondary"
                                size="sm"
                                type="button"
                                onClick={() => window.open(widgetTestUrl, '_blank', 'noopener,noreferrer')}
                              >
                                Abrir página de teste
                              </Button>
                            )}
                            <Button variant="secondary" size="sm" type="button" onClick={() => void copyEmbedScript()}>
                              Copiar
                            </Button>
                          </div>
                        </div>
                        <textarea readOnly value={embedScript} rows={7} className="w-full border border-gray-200 rounded-md px-3 py-2 text-xs font-mono bg-gray-50" />
                        <p className="text-xs text-gray-500 mt-2">
                          Cole este script em todas as páginas onde o chat deve aparecer. Visitante anônimo não precisa de mais nada.
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm text-gray-700">Identificar visitante logado (opcional)</label>
                          <Button variant="secondary" size="sm" type="button" onClick={() => void copyKnowWidgetInitExample()}>
                            Copiar
                          </Button>
                        </div>
                        <textarea
                          readOnly
                          value={knowWidgetInitExample}
                          rows={7}
                          className="w-full border border-gray-200 rounded-md px-3 py-2 text-xs font-mono bg-gray-50"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Se o visitante já estiver autenticado no site do cliente, o backend dele chama{' '}
                          <code className="font-mono">KnowWidget.init</code> depois do script de embed, com os dados reais da sessão
                          (substitua os valores de exemplo). Campos: <code className="font-mono">identifier</code> (ID interno),
                          <code className="font-mono"> identifierHash</code> (assinatura HMAC gerada no servidor),
                          <code className="font-mono"> name</code>, <code className="font-mono">email</code>, <code className="font-mono">phone</code>.
                          Com o pré-chat ligado, o formulário omite o que já veio no init. Sem HMAC válido, o ID enviado pelo browser não é prova de identidade.
                        </p>
                      </div>
                      {widgetTestUrl && (
                        <p className="text-xs text-gray-500">
                          Página de teste:{' '}
                          <a href={widgetTestUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                            {widgetTestUrl}
                          </a>
                          . O site que incorporar o widget precisa permitir esta API na Content-Security-Policy
                          (script, estilo e conexão WebSocket). O trecho de CSP aparece na página de teste.
                        </p>
                      )}
                    </div>
                  )}
                </AccordionSection>
              )}

              {showChannelConfigSection && (
                <AccordionSection
                  title="Configuração do canal"
                  description="Credenciais e parâmetros específicos deste canal"
                  icon={Cog6ToothIcon}
                  defaultOpen={false}
                >
                  {form.channelType === ChannelType.Whatsapp && (
                    <WhatsAppInboxConfigSection
                      config={channelConfig as WhatsAppInboxConfiguration}
                      onChange={patch => updateChannelConfig<WhatsAppInboxConfiguration>(patch)}
                      isEdit={isEdit}
                      inboxId={id}
                      occupiedPhoneDigits={occupiedPhoneDigits}
                      apiRootUrl={apiRootUrl}
                    />
                  )}

                  {form.channelType === ChannelType.Instagram && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">instagramAccountId</label>
                        <input
                          type="text"
                          value={(channelConfig as InstagramInboxConfiguration).instagramAccountId || ''}
                          onChange={e => updateChannelConfig<InstagramInboxConfiguration>({ instagramAccountId: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">pageAccessToken</label>
                        <input
                          type="password"
                          value={(channelConfig as InstagramInboxConfiguration).pageAccessToken || ''}
                          onChange={e => updateChannelConfig<InstagramInboxConfiguration>({ pageAccessToken: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          placeholder={isEdit && (channelConfig as InstagramInboxConfiguration).hasPageAccessToken ? '••••••••' : undefined}
                          autoComplete="new-password"
                        />
                        {isEdit && (channelConfig as InstagramInboxConfiguration).hasPageAccessToken && (
                          <p className="text-xs text-gray-500 mt-1">{KEEP_EXISTING_SECRET_HINT}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">verifyToken</label>
                        <input
                          type="text"
                          value={(channelConfig as InstagramInboxConfiguration).verifyToken || ''}
                          onChange={e => updateChannelConfig<InstagramInboxConfiguration>({ verifyToken: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">appSecret</label>
                        <input
                          type="password"
                          value={(channelConfig as InstagramInboxConfiguration).appSecret || ''}
                          onChange={e => updateChannelConfig<InstagramInboxConfiguration>({ appSecret: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          placeholder={isEdit && (channelConfig as InstagramInboxConfiguration).hasAppSecret ? '••••••••' : undefined}
                          autoComplete="new-password"
                        />
                        {isEdit && (channelConfig as InstagramInboxConfiguration).hasAppSecret && (
                          <p className="text-xs text-gray-500 mt-1">{KEEP_EXISTING_SECRET_HINT}</p>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">Webhook: POST {apiRootUrl}/api/webhooks/instagram</p>
                    </div>
                  )}

                  {form.channelType === ChannelType.Messenger && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">pageId</label>
                        <input
                          type="text"
                          value={(channelConfig as MessengerInboxConfiguration).pageId || ''}
                          onChange={e => updateChannelConfig<MessengerInboxConfiguration>({ pageId: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">pageAccessToken</label>
                        <input
                          type="password"
                          value={(channelConfig as MessengerInboxConfiguration).pageAccessToken || ''}
                          onChange={e => updateChannelConfig<MessengerInboxConfiguration>({ pageAccessToken: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          placeholder={isEdit && (channelConfig as MessengerInboxConfiguration).hasPageAccessToken ? '••••••••' : undefined}
                          autoComplete="new-password"
                        />
                        {isEdit && (channelConfig as MessengerInboxConfiguration).hasPageAccessToken && (
                          <p className="text-xs text-gray-500 mt-1">{KEEP_EXISTING_SECRET_HINT}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">verifyToken</label>
                        <input
                          type="text"
                          value={(channelConfig as MessengerInboxConfiguration).verifyToken || ''}
                          onChange={e => updateChannelConfig<MessengerInboxConfiguration>({ verifyToken: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">appSecret</label>
                        <input
                          type="password"
                          value={(channelConfig as MessengerInboxConfiguration).appSecret || ''}
                          onChange={e => updateChannelConfig<MessengerInboxConfiguration>({ appSecret: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          placeholder={isEdit && (channelConfig as MessengerInboxConfiguration).hasAppSecret ? '••••••••' : undefined}
                          autoComplete="new-password"
                        />
                        {isEdit && (channelConfig as MessengerInboxConfiguration).hasAppSecret && (
                          <p className="text-xs text-gray-500 mt-1">{KEEP_EXISTING_SECRET_HINT}</p>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">Webhook: POST {apiRootUrl}/api/webhooks/messenger</p>
                    </div>
                  )}

                  {form.channelType === ChannelType.Email && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">Host IMAP</label>
                          <input
                            type="text"
                            value={(channelConfig as EmailInboxConfiguration).imapHost || ''}
                            onChange={e => updateChannelConfig<EmailInboxConfiguration>({ imapHost: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Porta IMAP</label>
                          <input
                            type="number"
                            value={(channelConfig as EmailInboxConfiguration).imapPort ?? 993}
                            onChange={e => updateChannelConfig<EmailInboxConfiguration>({ imapPort: Number(e.target.value) })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={(channelConfig as EmailInboxConfiguration).imapUseSsl ?? true}
                              onChange={e => updateChannelConfig<EmailInboxConfiguration>({ imapUseSsl: e.target.checked })}
                            />
                            SSL
                          </label>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Usuário IMAP</label>
                          <input
                            type="text"
                            value={(channelConfig as EmailInboxConfiguration).imapUsername || ''}
                            onChange={e => updateChannelConfig<EmailInboxConfiguration>({ imapUsername: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Senha IMAP</label>
                          <input
                            type="password"
                            value={(channelConfig as EmailInboxConfiguration).imapPassword || ''}
                            onChange={e => updateChannelConfig<EmailInboxConfiguration>({ imapPassword: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            placeholder={isEdit && (channelConfig as EmailInboxConfiguration).hasImapPassword ? '••••••••' : undefined}
                            autoComplete="new-password"
                          />
                          {isEdit && (channelConfig as EmailInboxConfiguration).hasImapPassword && (
                            <p className="text-xs text-gray-500 mt-1">{KEEP_EXISTING_SECRET_HINT}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Remetente (e-mail)</label>
                          <input
                            type="email"
                            value={(channelConfig as EmailInboxConfiguration).fromAddress || ''}
                            onChange={e => updateChannelConfig<EmailInboxConfiguration>({ fromAddress: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Nome do remetente</label>
                          <input
                            type="text"
                            value={(channelConfig as EmailInboxConfiguration).fromName || ''}
                            onChange={e => updateChannelConfig<EmailInboxConfiguration>({ fromName: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </AccordionSection>
              )}

              <AccordionSection
                title="Atendimento"
                description="Robô, agentes e opções operacionais"
                icon={ChatBubbleLeftRightIcon}
              >
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">Robô de atendimento</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Selecione um robô para responder automaticamente nesta caixa.
                  </p>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Robô vinculado</label>
                  <select
                    value={selectedBotId}
                    onChange={e => setSelectedBotId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Nenhum robô</option>
                    {bots.map(bot => (
                      <option key={bot.id} value={bot.id}>{bot.name}</option>
                    ))}
                  </select>
                  {bots.length === 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Nenhum robô cadastrado.{' '}
                      <Link to={helpdeskRoutes.settingsBots} className="text-blue-600 hover:underline">
                        Criar robô
                      </Link>
                    </p>
                  )}
                </div>
                {selectedBotId && (
                  <p className="text-xs text-gray-500">
                    <Link to={helpdeskRoutes.settingsBots} className="text-blue-600 hover:underline">
                      Configurar regras de atuação
                    </Link>
                  </p>
                )}

                <div className="flex flex-col gap-3">
                  <ToggleField
                    label="Quebrar resposta longa do robô"
                    description={
                      isEmailChannel
                        ? 'Não se aplica a e-mail: a resposta do robô continua em uma única mensagem.'
                        : 'A resposta longa sai em várias bolhas. O tamanho é um alvo: a quebra desliza até a vírgula ou o ponto mais próximo.'
                    }
                    checked={!isEmailChannel && botReplySplitEnabled}
                    disabled={isEmailChannel}
                    onChange={checked => updateBotReplySplit({ enabled: checked })}
                  />
                  {!isEmailChannel && botReplySplitEnabled && (
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Tamanho alvo de cada bolha
                      </label>
                      <input
                        type="number"
                        min={BOT_REPLY_SPLIT_MIN_CHARACTERS}
                        max={BOT_REPLY_SPLIT_MAX_CHARACTERS_LIMIT}
                        value={botReplySplitMaxCharacters}
                        onChange={e => {
                          const parsed = Number.parseInt(e.target.value, 10);
                          if (Number.isNaN(parsed))
                          {
                            return;
                          }

                          updateBotReplySplit({ maxCharacters: parsed });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Mínimo {BOT_REPLY_SPLIT_MIN_CHARACTERS}, para uma saudação caber em uma bolha. Padrão {DEFAULT_BOT_REPLY_SPLIT_MAX_CHARACTERS}. Máximo {BOT_REPLY_SPLIT_MAX_CHARACTERS_LIMIT}. A bolha pode passar um pouco do alvo para terminar na pontuação. Sem pontuação por perto, o corte fica no espaço.
                      </p>
                    </div>
                  )}
                  {!isEmailChannel && botReplySplitEnabled && (
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Atraso entre bolhas (ms)
                      </label>
                      <input
                        type="number"
                        min={BOT_REPLY_SPLIT_MIN_DELAY_MS}
                        max={BOT_REPLY_SPLIT_MAX_DELAY_MS}
                        value={botReplySplitDelayMs}
                        onChange={e => {
                          const parsed = Number.parseInt(e.target.value, 10);
                          if (Number.isNaN(parsed))
                          {
                            return;
                          }

                          updateBotReplySplit({ delayMs: parsed });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Pausa entre as bolhas da mesma resposta. Padrão {DEFAULT_BOT_REPLY_SPLIT_DELAY_MS} ms. Use 0 para enviar todas de uma vez. Máximo {BOT_REPLY_SPLIT_MAX_DELAY_MS} ms.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-4 pt-2">
                  <ToggleField
                    label="CSAT habilitado"
                    description={
                      form.channelType === ChannelType.Email || form.channelType === ChannelType.Whatsapp
                        ? 'Ao resolver a conversa, envia um link público da pesquisa ao contato (e-mail ou WhatsApp).'
                        : 'Ao resolver a conversa (operador ou robô), oferece a pesquisa de satisfação no widget quando o canal tiver entrega suportada.'
                    }
                    checked={form.csatEnabled}
                    onChange={checked => setForm(f => ({ ...f, csatEnabled: checked }))}
                  />
                  {form.csatEnabled && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-3">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Pergunta da pesquisa</label>
                        <input
                          type="text"
                          value={form.csatSettings.message}
                          onChange={e => setForm(f => ({
                            ...f,
                            csatSettings: { ...f.csatSettings, message: e.target.value },
                          }))}
                          placeholder="Como você avalia o atendimento?"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Apresentação da nota</label>
                        <select
                          value={form.csatSettings.displayType}
                          onChange={e => setForm(f => ({
                            ...f,
                            csatSettings: {
                              ...f.csatSettings,
                              displayType: e.target.value as InboxCsatSettings['displayType'],
                            },
                          }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="emoji">Carinhas</option>
                          <option value="star">Estrelas</option>
                          <option value="number">Números 1–5</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Preview: {form.csatSettings.displayType === 'star'
                            ? '★ ★ ★ ★ ★'
                            : form.csatSettings.displayType === 'number'
                              ? '1 2 3 4 5'
                              : '😠 😕 😐 🙂 😄'}
                        </p>
                      </div>
                      <ToggleField
                        label="Pedir comentário"
                        description="Exibe um campo opcional de comentário junto da nota."
                        checked={form.csatSettings.askComment}
                        onChange={checked => setForm(f => ({
                          ...f,
                          csatSettings: { ...f.csatSettings, askComment: checked },
                        }))}
                      />
                      <ToggleField
                        label="Oferecer também na auto-resolução"
                        description="Se desligado, a pesquisa só é enviada quando operador ou robô encerram o atendimento. A resolução por inatividade não dispara CSAT."
                        checked={form.csatSettings.offerOnAutoResolve ?? false}
                        onChange={checked => setForm(f => ({
                          ...f,
                          csatSettings: { ...f.csatSettings, offerOnAutoResolve: checked },
                        }))}
                      />
                      {(form.channelType === ChannelType.Email || form.channelType === ChannelType.Whatsapp) && (
                        <div className="rounded-md border border-dashed border-gray-300 bg-white px-3 py-2">
                          <p className="text-xs font-medium text-gray-700">Link público da pesquisa</p>
                          <p className="mt-1 text-xs text-gray-500">
                            O contato recebe um link no formato abaixo. A primeira abertura é registrada mesmo se ele não responder.
                          </p>
                          <input
                            type="text"
                            readOnly
                            value={`${window.location.origin}/csat/{uuid-da-conversa}`}
                            className="mt-2 w-full border border-gray-200 rounded-md px-3 py-2 text-xs text-gray-600 bg-gray-50"
                          />
                        </div>
                      )}
                      {form.channelType === ChannelType.Whatsapp && (
                        <div className="flex flex-col gap-3">
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Template WhatsApp (Meta)</label>
                            <input
                              type="text"
                              value={form.csatSettings.whatsAppTemplateName ?? ''}
                              onChange={e => setForm(f => ({
                                ...f,
                                csatSettings: {
                                  ...f.csatSettings,
                                  whatsAppTemplateName: e.target.value,
                                },
                              }))}
                              placeholder="nome_do_template_aprovado"
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Obrigatório fora da janela de 24h. O botão de URL do template deve usar sufixo dinâmico com o UUID da conversa.
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Idioma do template</label>
                            <input
                              type="text"
                              value={form.csatSettings.whatsAppTemplateLanguage ?? 'pt_BR'}
                              onChange={e => setForm(f => ({
                                ...f,
                                csatSettings: {
                                  ...f.csatSettings,
                                  whatsAppTemplateLanguage: e.target.value,
                                },
                              }))}
                              placeholder="pt_BR"
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <ToggleField
                    label="Enter envia a mensagem"
                    description="Quando desativado, Enter quebra a linha e o envio é pelo botão ou Ctrl+Enter. Padrão: desativado em e-mail e ativado nos demais canais."
                    checked={enterSendsMessage}
                    onChange={checked => updateChannelConfig<InboxConfigurationBase>({ enterSendsMessage: checked })}
                  />
                </div>

                <MultiSelect
                  label="Agentes com acesso"
                  hint="Somente os agentes selecionados poderão ver e atender conversas desta caixa. Deixe vazio para permitir todos os usuários do tenant. Administradores sempre enxergam todas as caixas."
                  options={memberOptions}
                  selectedIds={form.memberUserIds}
                  onChange={memberUserIds => setForm(f => ({ ...f, memberUserIds }))}
                  placeholder="Buscar por nome ou e-mail..."
                  emptyMessage="Nenhum usuário encontrado"
                  showBulkActions
                />

                {form.channelType === ChannelType.Email && (
                  <p className="text-sm text-gray-600">
                    A assinatura do operador nas respostas é configurada em{' '}
                    <Link
                      to={helpdeskRoutes.settingsUserSignature}
                      className="text-blue-600 hover:underline"
                    >
                      Assinatura de e-mail
                    </Link>
                    . A assinatura do robô fica no cadastro do robô, na aba Assinatura.
                  </p>
                )}
              </AccordionSection>

              <AccordionSection
                title="Encerramento"
                description="Mensagem automática por inatividade"
                icon={ClockIcon}
              >
                <p className="text-xs text-gray-500">
                  Mensagem enviada ao contato quando a auto-resolução do tenant finalizar conversas desta caixa.
                  O tempo de inatividade é configurado em Configurações do Atendimento.
                </p>

                <ToggleField
                  label="Enviar mensagem automática ao encerrar por inatividade"
                  description="Quando desativado, a conversa é finalizada sem mensagem ao contato."
                  checked={form.sendClosingMessageOnAutoResolve}
                  onChange={checked => setForm(f => ({ ...f, sendClosingMessageOnAutoResolve: checked }))}
                />

                {form.sendClosingMessageOnAutoResolve && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mensagem de encerramento
                    </label>
                    <textarea
                      rows={8}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-900 focus:border-blue-900"
                      value={form.closingMessage}
                      onChange={e => setForm(f => ({ ...f, closingMessage: e.target.value }))}
                      placeholder={DEFAULT_CLOSING_MESSAGE_PLACEHOLDER}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Se deixar em branco, o sistema usará a mensagem padrão de encerramento.
                    </p>
                  </div>
                )}
              </AccordionSection>
            </div>

            {isWidgetChannel && (
              <aside className="hidden lg:block sticky top-4">
                <WidgetLivePreview {...previewProps} />
              </aside>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-200 bg-white px-1 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div>
                {isDirty ? (
                  <span className="text-sm text-amber-700">Alterações não salvas</span>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Link to={helpdeskRoutes.settingsInboxes}>
                  <Button variant="outline" type="button">Cancelar</Button>
                </Link>
                <Button variant="primary" type="submit" disabled={submitDisabled}>
                  {submitLabel}
                </Button>
              </div>
            </div>
          </div>
        </form>
      )}

      {isWidgetChannel && showGeneralTab && (
        <>
          <button
            type="button"
            className="lg:hidden fixed bottom-24 right-4 z-30 rounded-full bg-blue-600 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-blue-700"
            onClick={() => setPreviewModalOpen(true)}
          >
            Ver preview
          </button>
          <Modal
            isOpen={previewModalOpen}
            title="Prévia do widget"
            onClose={() => setPreviewModalOpen(false)}
          >
            <div className="max-h-[70vh] overflow-y-auto">
              <WidgetLivePreview {...previewProps} />
            </div>
          </Modal>
        </>
      )}

      <InboxCreateSupportFormModal
        open={createFormModalOpen}
        onClose={() => setCreateFormModalOpen(false)}
        addToast={addToast}
        onCreated={(summary) => {
          setPublishedForms((prev) => {
            if (prev.some((p) => p.id === summary.id))
            {
              return prev;
            }

            return [...prev, summary];
          });
          setLinkedForms((prev) => {
            if (prev.some((l) => l.formId === summary.id))
            {
              return prev;
            }

            return [
              ...prev,
              {
                formId: summary.id,
                formTitle: summary.title,
                formPublicId: summary.publicId,
                formStatus: summary.status,
                formAccessType: summary.accessType,
                sortOrder: prev.length,
                presentOnStart: false,
                isPreChat: false,
                presentAfterFirstMessage: false,
                blockMessagesUntilAnswered: false,
              },
            ];
          });
        }}
      />
    </div>
  );
};

function collectOccupiedWhatsAppPhones(inboxes: Inbox[], excludeInboxId?: string): string[] {
  return inboxes
    .filter(inbox => inbox.channelType === ChannelType.Whatsapp && inbox.id !== excludeInboxId)
    .map(inbox => {
      const config = parseInboxConfiguration<WhatsAppInboxConfiguration>(inbox.configuration);
      return normalizeWhatsAppPhoneDigits(config.phoneNumber);
    })
    .filter(digits => digits.length > 0);
}
